<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">
    <AppSidebar currentPage="messages" />

    <main class="flex-1 flex flex-col h-full overflow-hidden">
      <!-- Header -->
      <header class="h-16 bg-surface border-b border-slate-200 flex items-center justify-between px-4 md:px-4 md:px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Messages</h2>
          <p class="text-xs text-slate-500 hidden sm:block">Client communications and correspondence.</p>
        </div>

      </header>

      <div class="flex-1 overflow-hidden flex">
        <!-- Inbox List -->
        <div class="flex-shrink-0 border-r border-slate-200 bg-surface flex flex-col
          w-full md:w-80
          md:flex"
          :class="showInbox ? 'flex' : 'hidden md:flex'">
          <div class="p-4 border-b border-slate-100">
            <div class="relative">
              <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined" style="font-size: 18px;">search</span>
              <input v-model="searchQuery" type="text" placeholder="Search messages..."
                class="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <svg class="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>

          <!-- Message List -->
          <div v-else class="flex-1 overflow-y-auto">
            <div v-if="filteredThreads.length === 0" class="text-center py-12 text-slate-400">
              <span class="material-symbols-outlined text-slate-300" style="font-size: 40px;">chat</span>
              <p class="text-sm mt-2">No messages found</p>
            </div>
            <div v-for="thread in filteredThreads" :key="thread.clientName"
              @click="selectThread(thread)"
              class="p-4 border-b border-slate-100 cursor-pointer transition-colors hover:bg-slate-50"
              :class="selectedThread?.clientName === thread.clientName ? 'bg-primary/5 border-l-2 border-l-primary' : ''">
              <div class="flex items-center gap-3">
                <div class="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  :class="thread.avatarClass">
                  {{ thread.initials }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between mb-0.5">
                    <p class="text-sm font-semibold text-slate-900 truncate">{{ thread.clientName }}</p>
                    <span class="text-xs text-slate-400 flex-shrink-0 ml-2">{{ thread.lastDate }}</span>
                  </div>
                  <p class="text-xs text-slate-500 truncate">{{ thread.lastMessage }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Conversation Panel -->
        <div class="flex-1 flex flex-col" :class="showInbox ? 'hidden md:flex' : 'flex'">
          <!-- No selection -->
          <div v-if="!selectedThread" class="flex-1 flex items-center justify-center text-slate-400">
            <div class="text-center">
              <span class="material-symbols-outlined text-slate-300" style="font-size: 64px;">chat</span>
              <p class="mt-3 text-base font-medium">Select a conversation</p>
              <p class="text-sm text-slate-400 mt-1">Choose a client from the list to view messages</p>
            </div>
          </div>

          <!-- Conversation -->
          <template v-else>
            <!-- Conversation Header -->
            <div class="h-16 bg-surface border-b border-slate-200 flex items-center px-4 md:px-6 gap-3 flex-shrink-0">
              <!-- Back button — mobile only -->
              <button @click="showInbox = true; selectedThread = null"
                class="md:hidden flex items-center justify-center h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 flex-shrink-0">
                <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
              </button>
              <div class="h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" :class="selectedThread.avatarClass">
                {{ selectedThread.initials }}
              </div>
              <div>
                <p class="text-sm font-semibold text-slate-900">{{ selectedThread.clientName }}</p>
                <p class="text-xs text-slate-400">{{ selectedThread.messages.length }} messages</p>
              </div>
            </div>

            <!-- Messages -->
            <div class="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50">
              <div v-for="msg in selectedThread.messages" :key="msg.id"
                class="flex gap-3" :class="msg.isAdmin ? 'flex-row-reverse' : ''">
                <div class="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  :class="msg.isAdmin ? 'bg-primary text-secondary' : 'bg-blue-100 text-blue-700'">
                  {{ msg.isAdmin ? adminInitials : selectedThread.initials }}
                </div>
                <div class="max-w-[70%]">
                  <div class="px-4 py-3 rounded-2xl text-sm shadow-sm"
                    :class="msg.isAdmin ? 'bg-brand-dark text-white rounded-tr-sm' : 'bg-white text-slate-700 border border-slate-200 rounded-tl-sm'">
                    <template v-if="msg.fileUrl || msg.fileRef">
                      <a :href="buildFileUrl(msg)" target="_blank" rel="noopener"
                        class="flex items-center gap-2 underline underline-offset-2 break-all"
                        :class="msg.isAdmin ? 'text-blue-300 hover:text-blue-100' : 'text-blue-600 hover:text-blue-800'">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {{ msg.content }}
                      </a>
                    </template>
                    <template v-else>
                      {{ msg.content }}
                    </template>
                  </div>
                  <p class="text-xs text-slate-400 mt-1" :class="msg.isAdmin ? 'text-right' : ''">{{ msg.date }}</p>
                </div>
              </div>
            </div>

            <!-- Reply Box -->
            <div class="p-4 bg-surface border-t border-slate-200 flex-shrink-0">
              <div class="flex gap-3">
                <textarea v-model="replyText" rows="2" placeholder="Type your message..."
                  @keydown.enter.ctrl="sendReply"
                  class="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"></textarea>
                <button @click="sendReply" :disabled="sendingReply || !replyText.trim()"
                  class="px-5 py-2 rounded-xl bg-primary text-secondary font-bold text-sm hover:bg-primary-hover transition-all flex items-center gap-2 disabled:opacity-50 self-end">
                  <span v-if="!sendingReply" class="material-symbols-outlined" style="font-size: 18px;">send</span>
                  <svg v-else class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Send
                </button>
              </div>
              <p class="text-xs text-slate-400 mt-1">Ctrl+Enter to send</p>
            </div>
          </template>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import AppSidebar from '../components/AppSidebar.vue';
export default {
  name: 'AdminMessages',
  components: { AppSidebar },
  data() {
    return {
      loading: true,
      showInbox: true,
      searchQuery: '',
      threads: [],
      selectedThread: null,
      replyText: '',
      sendingReply: false,
      adminInitials: 'AD'
    };
  },
  computed: {
    filteredThreads() {
      if (!this.searchQuery) return this.threads;
      const q = this.searchQuery.toLowerCase();
      return this.threads.filter(t => t.clientName.toLowerCase().includes(q));
    }
  },
  async mounted() {
    try {
      const user = this.$sharepoint.getCurrentUser();
      if (user) {
        const name = user.name || user.username || 'Admin';
        const parts = name.split(' ');
        this.adminInitials = parts.length >= 2
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : name.substring(0, 2).toUpperCase();
      }
    } catch (error) {
  console.error('Error:', error);
}
    await this.loadMessages();
  },
  methods: {
    async loadMessages() {
      try {
        this.loading = true;
        const headers = await this.$sharepoint.getAuthHeaders();
        // ✅ Fixed field names: ClientName (not Client_x0020_Name), added SenderType
        const url = `${this.$sharepoint.baseUrl}/getbytitle('Client Messages')/items` +
          `?$select=Id,Title,Message,ClientName,Sender,SenderType,IsRead,FileUrl,Created` +
          `&$orderby=Created desc&$top=500`;
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const results = data.d?.results || [];

        // Group by client name
        const grouped = {};
        results.forEach(msg => {
          const client = msg.ClientName || 'Unknown';
          if (!grouped[client]) grouped[client] = [];
          grouped[client].push({
            id: msg.Id,
            content: msg.Message || msg.Title || '',
            // ✅ Use SenderType field — reliable way to know who sent it
            isAdmin: (msg.SenderType || '').toLowerCase() === 'admin',
            sender: msg.Sender || '',
            isRead: msg.IsRead,
            fileUrl: msg.FileUrl && !msg.FileUrl.includes('|') ? msg.FileUrl : null,
            fileRef: msg.FileUrl && msg.FileUrl.includes('|') ? msg.FileUrl : null,
            date: this.formatDate(msg.Created)
          });
        });

        const avatarClasses = ['bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-teal-100 text-teal-700', 'bg-orange-100 text-orange-700'];
        this.threads = Object.entries(grouped).map(([clientName, messages], i) => {
          const sorted = [...messages].reverse();
          const unread = messages.filter(m => !m.isRead && !m.isAdmin).length;
          return {
            clientName,
            initials: this.getInitials(clientName),
            avatarClass: avatarClasses[i % avatarClasses.length],
            messages: sorted,
            unread,
            lastMessage: sorted[sorted.length - 1]?.content || '',
            lastDate: sorted[sorted.length - 1]?.date || ''
          };
        });

        // Sort threads: unread first, then by last message date
        this.threads.sort((a, b) => {
          if (b.unread !== a.unread) return b.unread - a.unread;
          return 0;
        });

      } catch (error) {
        console.error('Error loading messages:', error);
        this.threads = [];
      } finally {
        this.loading = false;
      }
    },

    async selectThread(thread) {
      this.selectedThread = thread;
      // On mobile: hide the inbox list and show the conversation
      if (window.innerWidth < 768) this.showInbox = false;
      this.replyText = '';

      // Mark all unread client messages in this thread as read
      const unreadMsgs = thread.messages.filter(m => !m.isAdmin && !m.isRead);
      if (unreadMsgs.length > 0) {
        thread.unread = 0;
        unreadMsgs.forEach(m => { m.isRead = true; });
        // Persist to SharePoint in background
        for (const msg of unreadMsgs) {
          try { await this.$sharepoint.markMessageRead(msg.id); } catch (e) { /* non-fatal */ }
        }
        // Tell AppSidebar to refresh badge immediately
        window.dispatchEvent(new CustomEvent('messages-read'));
      }
    },

    async sendReply() {
      if (!this.replyText.trim() || !this.selectedThread) return;
      this.sendingReply = true;
      try {
        const user = this.$sharepoint.getCurrentUser();
        await this.$sharepoint.sendClientMessage({
          subject: `Re: ${this.selectedThread.clientName}`,
          message: this.replyText,
          clientName: this.selectedThread.clientName,
          sender: user?.name || user?.username || 'Admin',
          sendEmail: false
        });
        // Add to UI immediately
        this.selectedThread.messages.push({
          id: Date.now(),
          content: this.replyText,
          isAdmin: true,
          date: 'Just now'
        });
        this.replyText = '';
      } catch (error) {
        console.error('Error sending reply:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        this.sendingReply = false;
      }
    },

    getInitials(name) {
      if (!name) return '??';
      const parts = name.split(' ');
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    buildFileUrl(msg) {
      // "driveItemId|driveId" stored in fileRef — route through proxy, no SP login
      const ref = msg.fileRef || '';
      if (ref.includes('|')) {
        const [itemId, drvId] = ref.split('|');
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(drvId)}&itemId=${encodeURIComponent(itemId)}&admin=1`;
      }
      // Direct URL fallback (admin-sent files or older records)
      return msg.fileUrl || '#';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now - date) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    }
  }
};
</script>